import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import QRCode from "qrcode";
import { Loader2, Printer, ShieldX } from "lucide-react";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { api, apiError, fileUrl } from "../lib/api";

const CredencialPage = () => {
  const { attendeeId } = useParams();
  const [state, setState] = useState({ checking: true, denied: false, data: null, error: null });
  const [qr, setQr] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("hz_admin_token");
    if (!token) { setState({ checking: false, denied: false, data: null, error: null }); return; }
    api.get("/auth/me")
      .then((r) => {
        if (!["admin", "welcome_coordinator"].includes(r.data.role)) {
          setState({ checking: false, denied: true, data: null, error: null });
          return null;
        }
        return api.get(`/admin/attendees/${attendeeId}/credential`);
      })
      .then((r) => {
        if (r) setState({ checking: false, denied: false, data: r.data, error: null });
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem("hz_admin_token");
          setState({ checking: false, denied: false, data: null, error: null });
        } else {
          setState({ checking: false, denied: e?.response?.status === 403, data: null, error: apiError(e) });
        }
      });
  }, [attendeeId]);

  useEffect(() => {
    if (state.data?.qr_token) {
      QRCode.toDataURL(state.data.qr_token, { width: 220, margin: 1, color: { dark: "#2F557F", light: "#FFFFFF" } })
        .then(setQr).catch(() => {});
    }
  }, [state.data?.qr_token]);

  if (state.checking) return <div className="flex min-h-screen items-center justify-center text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!state.data && !state.denied && !state.error) return <Navigate to="/login?redirect=/admin" replace />;
  if (state.denied) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
        <ShieldX className="h-10 w-10 text-hz-error" />
        <p className="font-bold text-hz-navy" data-testid="credencial-denied">Tu rol no tiene acceso a credenciales.</p>
      </div>
    );
  }
  if (state.error) return <div className="flex min-h-screen items-center justify-center"><p className="font-bold text-hz-error">{state.error}</p></div>;

  const d = state.data;
  const accesses = [
    "Evento general",
    ...(d.dinner_selected ? ["Cena especial"] : []),
    ...d.workshops.map((w) => w.name),
  ];

  return (
    <div className="flex min-h-screen flex-col items-center bg-hz-bg p-6" data-testid="credencial-page">
      <div className="w-full max-w-sm overflow-hidden rounded-3xl border-2 border-hz-navy bg-white shadow-lg" data-testid="credencial-card">
        <div className="bg-hz-navy px-6 py-4 text-center">
          <p className="text-base font-extrabold tracking-wide text-white">HORIZONTE 2026</p>
          <p className="text-[9px] font-semibold uppercase tracking-widest text-hz-turquoise">Liderazgo con Visión Reformada</p>
        </div>
        <div className="flex flex-col items-center px-6 py-5">
          {d.photo_url ? (
            <img src={fileUrl(d.photo_url)} alt={`Fotografía de ${d.full_name}`} className="h-24 w-24 rounded-2xl border-2 border-hz-cyan/40 object-cover" data-testid="credencial-photo" />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-hz-gray/30 text-xs font-bold text-hz-ink/50">Sin foto</div>
          )}
          <p className="mt-3 text-center text-lg font-extrabold text-hz-ink" data-testid="credencial-name">{d.full_name}</p>
          <p className="text-sm font-extrabold tracking-wider text-hz-cyan" data-testid="credencial-folio">{d.folio}</p>
          <p className="mt-1 text-xs font-semibold text-hz-ink/60">{d.delegation_name}</p>
          <div className="my-4 flex w-full flex-wrap justify-center gap-1.5">
            {accesses.map((a) => (
              <span key={a} className="rounded-full bg-hz-cyan/10 px-2.5 py-1 text-[10px] font-bold text-hz-blue">{a}</span>
            ))}
          </div>
          {d.access_granted && qr ? (
            <img src={qr} alt={`Código QR de acceso de ${d.full_name}`} className="h-40 w-40" data-testid="credencial-qr" />
          ) : (
            <p className="rounded-xl border border-hz-gray/40 bg-hz-bg px-4 py-3 text-center text-xs font-semibold text-hz-ink/60" data-testid="credencial-no-qr">
              Código de acceso disponible después de confirmar el pago
            </p>
          )}
        </div>
      </div>
      <Button onClick={() => window.print()} data-testid="credencial-print-btn" className="mt-6 bg-hz-navy font-bold text-white hover:bg-hz-blue print:hidden">
        <Printer className="mr-2 h-4 w-4" /> Imprimir / Guardar PDF
      </Button>
    </div>
  );
};

export default CredencialPage;
